<template>
  <b-card>
    <b-card-title class="d-flex justify-content-between">
      <h3>Trip Path</h3>
      <b-button
        v-if="driverLocation"
        @click="viewDriverLocation"
      >
        View Driver Location
      </b-button>
      <div
        v-if="googlePath.length"
        class="d-flex"
      >
        <div
          class="d-flex align-items-center mr-1"
        >
          <div
            class="mr-1 rounded"
            style="width:10px;height:10px;background:#0000FF"
          />
          <span>Actual Path</span>
        </div>
        <div class="d-flex align-items-center mr-1">
          <div
            class="mr-1 rounded"
            style="width:10px;height:10px;background:#FF0000"
          />
          <span>Google Path</span>
        </div>
        <div
          v-if="orsPath.length"
          class="d-flex align-items-center"
        >
          <div
            class="mr-1 rounded"
            style="width:10px;height:10px;background:black"
          />
          <span>ORS Path</span>
        </div>
      </div>
    </b-card-title>
    <div id="TripMap" />
  </b-card>
</template>

<script>
/* eslint-disable no-undef */
import {
  onMounted, ref, inject, computed, watch,
} from '@vue/composition-api'
import { BCard, BCardTitle, BButton } from 'bootstrap-vue'
import { decode } from '@googlemaps/polyline-codec'
import { tripStatusFormatter, tripPathMarkerIcon, tripColorGenerator } from '../../hooks/tripStatusUtils'
import petraP from '@/assets/images/icons/petra-p.svg'

export default {
  components: {
    BCard,
    BCardTitle,
    BButton,
  },
  props: {
    tripPath: {
      type: Array,
      default: () => [],
    },
    driverLocation: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const TRIP_PATH_PIN_STATES = ['ACCEPTED', 'ARRIVED', 'COMPLETED', 'IN_PROGRESS', 'CANCELLED']

    const trip = inject('trip')

    const googlePath = ref([])
    const driverLocationMarker = ref(null)

    const stopPoints = computed(() => trip.value?.metadata?.stop_points || [])
    const orsPath = computed(() => {

      try {
        return trip.value?.metadata?.ors_directions?.results?.features[0]?.geometry?.coordinates || []
      } catch (error) {
        return []
      }
    })
    const tripStates = computed(() => trip.value?.states || [])

    const pickUpLocationCoordinates = computed(() => {
      const tripPickup = trip.value?.metadata?.location?.pickup

      return tripPickup ? {
        lat: +tripPickup.latitude,
        lng: +tripPickup.longitude,
      } : null
    })

    const map = ref(null)
    const refreshButton = ref(null)
    const createTripRefreshButton = () => {
      refreshButton.value = document.createElement('div')
      const controlUI = document.createElement('div')

      controlUI.style.backgroundColor = '#fff'
      controlUI.style.border = '2px solid #fff'
      controlUI.style.borderRadius = '3px'
      controlUI.style.boxShadow = '0 2px 6px rgba(0,0,0,.3)'
      controlUI.style.cursor = 'pointer'
      controlUI.style.marginTop = '8px'
      controlUI.style.marginBottom = '22px'
      controlUI.style.textAlign = 'center'
      controlUI.title = 'Click to recenter the map'
      refreshButton.value.appendChild(controlUI)

      const controlText = document.createElement('div')

      controlText.style.color = 'green'
      controlText.style.fontSize = '16px'
      controlText.style.lineHeight = '38px'
      controlText.style.paddingLeft = '10px'
      controlText.style.paddingRight = '10px'
      controlText.innerHTML = 'Refresh Path'
      controlUI.appendChild(controlText)
      controlUI.addEventListener('click', () => {
        emit('refetch-path')
      })
      map.value.controls[google.maps.ControlPosition.TOP_CENTER].push(refreshButton.value)
    }

    const createPickupLocationMarker = () => {
      const contentString = '<div id="content" style="width:200px">'
         + '<h5 style="margin-top:5px">Pick Up Location</h5>'
         + '</div>'
      const infowindow = new google.maps.InfoWindow({
        content: contentString,
      })
      const marker = new google.maps.Marker({
        position: pickUpLocationCoordinates.value,
      })
      marker.addListener('click', () => {
        infowindow.open({
          anchor: marker,
          map,
        })
      })
      marker.setMap(map.value)
    }

    const createDriverLocationCar = () => {
      if (driverLocationMarker.value) {
        driverLocationMarker.value.setMap(null)
      }
      const marker = new google.maps.Marker({
        position: {
          lat: +props.driverLocation.lat,
          lng: +props.driverLocation.lon,
        },
        icon: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png', // Default blue marker for current driver location
      })
      marker.setMap(map.value)
      driverLocationMarker.value = marker
    }
    const createMarker = coordinate => {
      const contentString = '<div id="content" style="width:200px">'
    + `<h5  class="text-${tripColorGenerator(coordinate.state)}" style="margin-top:5px">${tripStatusFormatter(coordinate.state)}</h5>`
    + '</div>'
      const infowindow = new google.maps.InfoWindow({
        content: contentString,
      })
      const marker = new google.maps.Marker({
        position: {
          lat: +coordinate.lat,
          lng: +coordinate.lng,
        },
        icon: tripPathMarkerIcon(coordinate.state),
      })
      marker.addListener('click', () => {
        infowindow.open({
          anchor: marker,
          map,
        })
      })
      marker.setMap(map.value)
    }

    const initMap = () => {
      map.value = new google.maps.Map(document.getElementById('TripMap'), {
        center: {
          lat: 40,
          lng: 40,
        },
        zoom: 17,
      })
    }

    const initTripPaths = () => {
      let paths = []
      const availableStates = []
      if (props.tripPath && props.tripPath.length) {
        paths = props.tripPath.reduce((acc, curr, ix) => {
          if (props.tripPath.length - 1 === ix && trip.value.state === 'COMPLETED') {
            // Trip is completed hence the completed coordinate is the last in progress coordinate
            createMarker({
              lat: curr.lat,
              lng: curr.lng,
              state: 'COMPLETED',
            })
          } else if (!availableStates.includes(curr.state) && TRIP_PATH_PIN_STATES.includes(curr.state)) {
            availableStates.push(curr.state)
            createMarker(curr)
          }
          return [
            ...acc,
            {
              lat: +curr.lat,
              lng: +curr.lng,
            },
          ]
        }, [])
        const polyline = new google.maps.Polyline({
          path: paths,
          geodesic: true,
          strokeColor: '#0000FF',
          strokeOpacity: 1.0,
          strokeWeight: 3,
        })

        polyline.setMap(map.value)
      }
      if (trip.value.googlePath) {
        googlePath.value = []
        trip.value.googlePath.forEach(p => {
          decode(p.polyline.points).forEach(el => {
            googlePath.value.push({
              lat: el[0],
              lng: el[1],
            })
          })
        })
      } else if (trip.value.orsPath) {
        googlePath.value = []
        trip.value.orsPath.forEach(p => {
          googlePath.value.push({
            lat: p[1],
            lng: p[0],
          })
        })
      }
      const googlePolyLine = new google.maps.Polyline({
        path: googlePath.value,
        geodesic: true,
        strokeColor: '#FF0000',
        strokeOpacity: 1.0,
        strokeWeight: 3,
      })
      googlePolyLine.setMap(map.value)
    }
    const viewDriverLocation = () => {
      map.value.setCenter({
        lat: +props.driverLocation.lat,
        lng: +props.driverLocation.lon,
      })
    }
    const initOptionsAndMapCenter = () => {
      initTripPaths()
      if (props.driverLocation) {
        createDriverLocationCar()
      }
      if (!refreshButton.value) {
        createTripRefreshButton()
      }
      if (pickUpLocationCoordinates.value) {
        createPickupLocationMarker()
      }
      if (props.tripPath.length) {
        map.value.setCenter({
          lat: +props.tripPath[0].lat,
          lng: +props.tripPath[0].lng,
        })
      } else if (trip.value.googlePath) {
        map.value.setCenter({
          lat: +googlePath.value[0].lat,
          lng: +googlePath.value[0].lng,
        })
      } else if (trip.value.orsPath) {
        map.value.setCenter({
          lat: +googlePath.value[0].lat,
          lng: +googlePath.value[0].lng,
        })
      } else {
        map.value.setCenter({
          lat: +props.driverLocation.lat,
          lng: +props.driverLocation.lon,
        })
      }
    }

    const createStopPoints = () => {
      stopPoints.value.forEach((p, ix) => {
        const marker = new google.maps.Marker({
          position: {
            lat: +p.location.latitude,
            lng: +p.location.longitude,
          },
          icon: {
            url: petraP,
            scaledSize: new google.maps.Size(50, 50),
            origin: new google.maps.Point(0, 0),
            anchor: new google.maps.Point(0, 0),
          },
        })
        marker.setMap(map.value)
        const CustomBox = new google.maps.OverlayView()
        CustomBox.onAdd = function onAdd() {
          const div = document.createElement('div')
          div.style.position = 'absolute'
          div.style.backgroundColor = '#043CA1'
          div.style.color = 'white'
          div.style.padding = '5px'
          div.style.borderRadius = '4px'
          div.style.whiteSpace = 'nowrap'
          div.textContent = `Stop ${ix + 1}`
          this.div = div

          const panes = this.getPanes()
          panes.overlayImage.appendChild(div)
        }

        CustomBox.draw = function draw() {
          const projection = this.getProjection()
          const position = projection.fromLatLngToDivPixel(
            new google.maps.LatLng(+p.location.latitude, +p.location.longitude),
          )
          if (this.div) {
            this.div.style.left = `${position.x - this.div.offsetWidth / 2 + 25}px`
            this.div.style.top = `${position.y + 45}px`
          }
        }

        CustomBox.onRemove = function onRemove() {
          if (this.div) {
            this.div.parentNode.removeChild(this.div)
            this.div = null
          }
        }

        CustomBox.setMap(map.value)
      })
    }

    const createOrsPath = () => {
      if (orsPath.value.length) {
        const orsPathTemp = []

        orsPath.value.forEach(el => {
          orsPathTemp.push({
            lat: el[1],
            lng: el[0],
          })
        })
        const googlePolyLine = new google.maps.Polyline({
          path: orsPathTemp,
          geodesic: true,
          strokeColor: 'black',
          strokeOpacity: 1.0,
          strokeWeight: 3,
        })
        googlePolyLine.setMap(map.value)
      }
    }

    const createDriverStateMarkers = () => {
    console.log(tripStates.value);
    
      tripStates.value.forEach(state => {
        if (state.metadata && state.metadata.driver_latitude && state.metadata.driver_longitude) {
          const contentString = '<div id="content" style="width:250px">'
            + `<h5 style="margin-top:5px">Actual Driver Location (${state.state})</h5>`
            + `<p>Action by: ${state.metadata.action_by}</p>`
            + `<p>At: ${new Date(state.created_at).toLocaleString()}</p>`
            + '</div>'

          const infowindow = new google.maps.InfoWindow({
            content: contentString,
          })

          const marker = new google.maps.Marker({
            position: {
              lat: +state.metadata.driver_latitude,
              lng: +state.metadata.driver_longitude,
            },
            icon: tripPathMarkerIcon(state.state),
            title: `Driver Location - ${state.state}`,
          })

          marker.addListener('click', () => {
            infowindow.open({
              anchor: marker,
              map: map.value,
            })
          })

          marker.setMap(map.value)
        }
      })
    }

    onMounted(() => {
      initMap()
      initOptionsAndMapCenter()
      createStopPoints()
      createOrsPath()
      createDriverStateMarkers()
    })
    watch(() => props.driverLocation, () => {
      createDriverLocationCar()
    })
    watch(() => props.tripPath, () => {
      initOptionsAndMapCenter()
    })
    watch(() => tripStates.value, () => {
      createDriverStateMarkers()
    }, { deep: true })
    return {
      googlePath,
      orsPath,
      viewDriverLocation,
    }
  },
}

</script>

<style scoped>
#TripMap {
  width: 100%;
  height: 400px;
  margin-bottom: 2rem;
}
</style>
